import { Button, Input, Modal, Row, Col, message, Spin } from "antd";
import FetchSelect from "Components/fetch-select";
import React, { useEffect } from "react";
import { Controller, useFieldArray, useForm, watch } from "react-hook-form";
import { RealEstateStoreModel, ACTION_TYPES } from "store/models/real-estates";
import { fixDates, clean } from "Utils/";
import RealEstateSelect from "Components/realestate-select";
import Yup from "ValidationModels/localizedYup";
import { createComponentStore } from "easy-peasy";
import { ParameterStoreModel } from "store/models/parameters/general-parameter";
import FormLayout, { FormSection, FormItem } from "Components/form";
import DatePicker from "Components/date-picker-localized";
import { yupResolver } from "@hookform/resolvers";
import DeedTypeSelect from "Components/deed-type-select";
import { RULES } from "Constants/rules";
import ShareholderList from "../shareholders-list";
import { DISPLAY_MODE } from "Constants";

const useRealEstateStore = createComponentStore(RealEstateStoreModel);
const useDeedTypeStore = createComponentStore(ParameterStoreModel("dedtypes"));

Yup.addMethod(Yup.array, "PercentageCheck", function (message) {
  return this.test("unique", message, function (value) {
    if (!!!value) return true;
    if (value.length <= 0) return true;

    const shareHolders = [...value];
    let total = 0;
    shareHolders.forEach((item) => {
      total = total + item.percentage;
    });

    if (total != 100)
      throw this.createError({
        path: `${this.path}`,
        message,
      });

    return true;
  });
});

const FormValidationSchema = Yup.object().shape({
  administration: Yup.string(),
  allocationPurpose: Yup.object({
    value: Yup.number(),
    label: Yup.string(),
  }).nullable(),
  archiveNumber: Yup.string().nullable(),
  dateOfAcquisition: Yup.date().nullable(),
  dateOfArchive: Yup.date().nullable(),
  dateOfEntry: Yup.date().nullable().required(),
  dateOfOutput: Yup.date().nullable(),
  costPrice: Yup.string().matches("^[-,0-9]+$", "Sadece rakam girebilirsiniz."),
  // marketValue: Yup.string().matches(
  //   "^[-,0-9]+$",
  //   "Sadece rakam girebilirsiniz."
  // ),
  marketValue: Yup.number().transform((_, value) => {
    if (value.includes(".")) {
      return null;
    }
    return +value.replace(/,/, ".");
  }),
  tokenValue: Yup.number().transform((_, value) => {
    if (value.includes(".")) {
      return null;
    }
    return +value.replace(/,/, ".");
  }),
  taxValue: Yup.number().transform((_, value) => {
    if (value.includes(".")) {
      return null;
    }
    return +value.replace(/,/, ".");
  }),
  deeds: Yup.array().of(
    Yup.object({
      avenue: Yup.string(),
      buildingNumber: Yup.string(),
      dateOfRegistration: Yup.date(),
      sequence: Yup.string(),
      east: Yup.string(),

      floorspace: Yup.number().transform((_, value) => {
        if (value.includes(".")) {
          return null;
        }
        return +value.replace(/,/, ".");
      }),

      islandNumber: Yup.string(),
      locality: Yup.string(),
      north: Yup.string(),
      orderNumber: Yup.string(),
      pageNumber: Yup.string(),
      parcelNumber: Yup.string(),
      shareRate: Yup.number(),
      sheetNumber: Yup.string(),
      south: Yup.string(),
      street: Yup.string(),
      type: Yup.object().nullable().required(),
      village: Yup.string(),
      vol: Yup.string(),
      west: Yup.string(),
    })
  ),
  durationOfAcquisition: Yup.string().nullable(),
  description: Yup.string().max(1000),
  legalStatu: Yup.object({
    id: Yup.number(),
  }).nullable(),
  shareHolders: Yup.array()
    .of(
      Yup.object({
        shareHolder: Yup.object().nullable().required(),
        percentage: Yup.number().positive().required(),
      })
    )
    .PercentageCheck("Hissedarların yüzde dağılım toplamları 100 olmalıdır"),
  name: Yup.string().required(),
  organization: Yup.object({
    id: Yup.number().required(),
  }).nullable(),
  outputShape: Yup.object({
    id: Yup.number(),
  }).nullable(),
  realEstateRecordType: Yup.object({
    id: Yup.number().required(),
  }).nullable(),
  realEstateStatu: Yup.object({
    id: Yup.number(),
  }).nullable(),
});

export default function RealEstateForm({
  realestate,
  onAfterSuccess,
  loading = false,
  mode = DISPLAY_MODE.READ_ONLY,
  ...props
}) {
  const [store, actions] = useRealEstateStore();
  const [deedTypeStore, deedTypeActions] = useDeedTypeStore();

  const form = useForm({
    mode: "onChange",
    resolver: yupResolver(FormValidationSchema),
    defaultValues: realestate,
  });

  const { control, handleSubmit, errors, watch, reset } = form;

  const { fields: deeds, append: appendDeed } = useFieldArray({
    control: control,
    name: "deeds",
    keyName: "key",
  });

  const selectedDeedType = watch("deeds[0].type");

  useEffect(() => {
    if (realestate) {
      let payload = fixDates({ ...realestate });
      //doble ve float alanlarda nokta'dan virgüle çevirim düzeltmesi
      if (payload.deeds[0] && payload.deeds[0].floorspace)
        payload.deeds[0].floorspace = payload.deeds[0].floorspace
          .toString()
          .replace(".", ",");

      let convertibleFields = ["taxValue", "marketValue", "tokenValue"];

      convertibleFields.forEach((i) => {
        if (payload[i]) payload[i] = payload[i].toString().replace(".", ",");
      });
      console.log(payload);
      reset(payload);
    }
  }, [realestate, reset]);

  const IsLoading = (process) => {
    return [ACTION_TYPES.FETCHING_DATA].includes(process);
  };

  useEffect(() => {
    deedTypeActions.fetch();
  }, []);

  useEffect(() => {
    if (selectedDeedType) {
      if (!selectedDeedType.hasOwnProperty("root")) {
        selectedDeedType.root = {
          ...deedTypeStore.data.find(
            (f) => f.code == selectedDeedType.code.charAt(0)
          ),
        };
      }
    }
  }, [selectedDeedType]);

  useEffect(() => {
    if (store.response)
      switch (store.process) {
        case ACTION_TYPES.CREATED_DATA:
          message.success("Taşınmaz oluşturuldu.");
          onAfterSuccess(store.response);
          break;
        case ACTION_TYPES.UPDATED_DATA:
          message.success("Taşınmaz güncellendi.");
          onAfterSuccess(store.response);
          break;
        default:
          break;
      }
  }, [store.process, store.response]);

  const formOnSubmit = async (form) => {
    clean(form);

    if (mode === DISPLAY_MODE.EDIT) {
      let payload = {
        ...realestate,
        ...form,
        deeds: form.deeds.map((deed) => {
          if (deed.id)
            return {
              ...realestate.deeds.find((f) => f.id === deed.id),
              ...deed,
            };

          return deed;
        }),
        shareHolders: form.shareHolders?.map((element) => {
          if (element.id)
            return {
              ...realestate.shareHolders?.find((f) => f.id === element.id),
              ...element,
            };

          return element;
        }),
      };

      actions.update(payload);
    }
    if (mode === DISPLAY_MODE.NEW) actions.create(form);
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      message.error("Taşınmaz için girdiğiniz değerleri kontrol ediniz.");
    }
  }, [errors]);

  const renderDeedFields = (index) => {
    return (
      <>
        <Controller
          control={control}
          name={`deeds[${index}].id`}
          as={<Input type="hidden" />}
        />
        {/*deeds[index]?.sequence === "PRESENT_INFORMATION" ? (
          <Col xs={24} md={12}>
            <FormItem>
              <Button
                onClick={() => {
                  let values = form.getValues().deeds[1];
                  delete values.sequence;
                  delete values.id;
                  Object.keys(values).forEach((key) => {
                    if (!form.getValues().deeds[0][key])
                      form.setValue("deeds[0]." + key, values[key]);
                  });
                }}
              >
                Sınıflandırmadan Devral
              </Button>
            </FormItem>
          </Col>
        ) : null*/}

        <Col xs={24} md={12}>
          <FormItem
            htmlFor="deed.type"
            required={
              deeds[index]?.sequence === "PRESENT_INFORMATION" ? true : false
            }
            error={errors.deeds && errors.deeds[index]?.type}
            label="Taşınmaz Cinsi"
          >
            <DeedTypeSelect
              form={form}
              readOnly={mode === DISPLAY_MODE.READ_ONLY}
              field={`deeds[${index}].type`}
              placeholder="Taşınmaz Cinsi seçiniz"
            />
          </FormItem>
        </Col>
        <Col xs={24} md={12}>
          <FormItem
            label="Tescil Tarihi"
            error={errors.deeds && errors.deeds[index]?.dateOfRegistration}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].dateOfRegistration`}
              as={
                <DatePicker
                  format="DD.MM.YYYY"
                  style={{ width: "100%" }}
                  readOnly={mode === DISPLAY_MODE.READ_ONLY}
                  placeholder="Tescil tarihi giriniz"
                />
              }
            />
          </FormItem>
        </Col>

        {[
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(selectedDeedType?.root?.rule?.code) ? (
          <Col xs={24} md={6}>
            <FormItem label="Takbis Zemin No">
              <Controller
                control={form.control}
                name="takbisNumber"
                as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
              />
            </FormItem>
          </Col>
        ) : null}

        <Col xs={24} md={6}>
          <FormItem label="Taşınmaz No">
            <Controller
              control={form.control}
              name="realEstateNumber"
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={6}>
          <FormItem label="İl">
            <Controller
              control={form.control}
              name={`deeds[${index}].province`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={6}>
          <FormItem label="İlçe">
            <Controller
              control={form.control}
              name={`deeds[${index}].district`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={6}>
          <FormItem label="Mahalle">
            <Controller
              control={form.control}
              name={`deeds[${index}].neighborhood`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>
        <Col xs={24} md={6}>
          <FormItem
            label="Köy"
            error={errors.deeds && errors.deeds[index]?.village}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].village`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        <Col xs={24} md={6}>
          <FormItem
            label="Yüzölçümü"
            error={errors.deeds && errors.deeds[index]?.floorspace}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].floorspace`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <FormItem
            label="Pafta No."
            error={errors.deeds && errors.deeds[index]?.sheetNumber}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].sheetNumber`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        {[
          RULES.RULE0009,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(selectedDeedType?.root?.rule?.code) ? (
          <>
            <Col xs={24} sm={12} md={6}>
              <FormItem
                label="Ada No."
                error={errors.deeds && errors.deeds[index]?.islandNumber}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].islandNumber`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FormItem
                label="Parsel No."
                error={errors.deeds && errors.deeds[index]?.parcelNumber}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].parcelNumber`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FormItem
                label="Cilt No."
                error={errors.deeds && errors.deeds[index]?.vol}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].vol`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FormItem
                label="Sayfa No."
                error={errors.deeds && errors.deeds[index]?.pageNumber}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].pageNumber`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <FormItem
                label="Sıra No."
                error={errors.deeds && errors.deeds[index]?.orderNumber}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].orderNumber`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
          </>
        ) : null}

        <Col xs={24} sm={12} md={6}>
          <FormItem
            label="Doğusu"
            error={errors.deeds && errors.deeds[index]?.east}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].east`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormItem
            label="Batısı"
            error={errors.deeds && errors.deeds[index]?.west}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].west`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormItem
            label="Güneyi"
            error={errors.deeds && errors.deeds[index]?.south}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].south`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <FormItem
            label="Kuzeyi"
            error={errors.deeds && errors.deeds[index]?.north}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].north`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        {[
          RULES.RULE0010,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(selectedDeedType?.root?.rule?.code) ? (
          <>
            <Col xs={24} md={6}>
              <FormItem
                label="Mevkii / Yöre"
                error={errors.deeds && errors.deeds[index]?.locality}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].locality`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>

            <Col xs={24} md={6}>
              <FormItem
                label="Cadde"
                error={errors.deeds && errors.deeds[index]?.avenue}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].avenue`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
            <Col xs={24} md={6}>
              <FormItem
                label="Sokak"
                error={errors.deeds && errors.deeds[index]?.street}
              >
                <Controller
                  control={form.control}
                  name={`deeds[${index}].street`}
                  as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
                />
              </FormItem>
            </Col>
          </>
        ) : null}

        <Col xs={24} md={6}>
          <FormItem
            label="Bina No."
            error={errors.deeds && errors.deeds[index]?.buildingNumber}
          >
            <Controller
              control={form.control}
              name={`deeds[${index}].buildingNumber`}
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        {[
          RULES.RULE0010,
          RULES.RULE0011,
          RULES.RULE0012,
          RULES.RULE0013,
        ].includes(selectedDeedType?.root?.rule?.code) ? (
          <Col xs={24} md={6}>
            <FormItem
              htmlFor="allocationPurpose"
              error={errors.allocationPurpose}
              label="Mevcut Kullanım Şekli/Tahsis Amacı"
            >
              <FetchSelect
                api="/masterdata/allocationpurposes"
                form={form}
                readOnly={mode === DISPLAY_MODE.READ_ONLY}
                field={`allocationPurpose`}
                placeholder="Mevcut Kullanım Şekli/Tahsis Amacı Seçiniz"
              />
            </FormItem>
          </Col>
        ) : null}

        <Col xs={24} md={6}>
          <FormItem label="İz Bedeli" error={errors.tokenValue}>
            <Controller
              control={form.control}
              name="tokenValue"
              as={<Input readOnly={mode === DISPLAY_MODE.READ_ONLY} />}
            />
          </FormItem>
        </Col>

        {[RULES.RULE0009, RULES.RULE0013].includes(
          selectedDeedType?.root?.rule?.code
        ) ? (
          <Col xs={24} md={6}>
            <FormItem label="Emlak Vergi Değeri" error={errors.taxValue}>
              <Controller
                control={form.control}
                readOnly={mode === DISPLAY_MODE.READ_ONLY}
                name="taxValue"
                as={<Input />}
              />
            </FormItem>
          </Col>
        ) : null}

        {selectedDeedType &&
        [RULES.RULE0009, RULES.RULE0013].includes(
          selectedDeedType?.root?.rule?.code
        ) ? (
          <Col xs={24} md={6}>
            <FormItem label="Rayiç Bedeli" error={errors.marketValue}>
              <Controller
                control={form.control}
                readOnly={mode === DISPLAY_MODE.READ_ONLY}
                name="marketValue"
                as={<Input />}
              />
            </FormItem>
          </Col>
        ) : null}

        {[RULES.RULE0009, RULES.RULE0013].includes(
          selectedDeedType?.root?.rule?.code
        ) ? (
          <Col xs={24} md={6}>
            <FormItem label="Maliyet Bedeli" error={errors.costPrice}>
              <Controller
                control={form.control}
                readOnly={mode === DISPLAY_MODE.READ_ONLY}
                name="costPrice"
                as={<Input />}
              />
            </FormItem>
          </Col>
        ) : null}
      </>
    );
  };

  return (
    <Spin spinning={IsLoading(store.process)}>
      <form onSubmit={handleSubmit(formOnSubmit)} style={{ margin: "0 24px" }}>
        <FormLayout>
          <Col xs={24}>
            <FormSection title="Genel">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <FormItem label="Ad" error={errors.name} required={true}>
                    <Controller
                      control={control}
                      name="name"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      as={<Input placeholder="Ad giriniz" />}
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem
                    label="Giriş Tarihi"
                    error={errors.dateOfEntry}
                    required={true}
                  >
                    <Controller
                      control={control}
                      name="dateOfEntry"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      as={
                        <DatePicker
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          placeholder="Giriş tarihi giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24}>
                  <FormItem label="Açıklama" error={errors.description}>
                    <Controller
                      control={control}
                      name="description"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      as={
                        <Input.TextArea
                          rows={3}
                          placeholder="Açıklama yazınız"
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="realEstateRecordType"
                    required={true}
                    error={errors.realEstateRecordType?.id}
                    label="Taşınmaz Kayıt Türü"
                  >
                    <FetchSelect
                      api="/masterdata/realestatetype"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field="realEstateRecordType"
                      placeholder="Taşınmaz kayıt türü seçiniz"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="realEstateQualification"
                    label="Taşınmaz Niteliği Türü"
                  >
                    <FetchSelect
                      api="/masterdata/realestatequalifications"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field="realEstateQualification"
                      placeholder="Taşınmaz niteliği seçiniz"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="realEstateStatu"
                    error={errors.realEstateStatu}
                    label="Taşınmaz Durumu"
                  >
                    <FetchSelect
                      api="/masterdata/realestatestatu"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field="realEstateStatu"
                      placeholder="Taşınmaz durumu seçiniz"
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={8}>
                  <FormItem label="Yönetim" error={errors.administration}>
                    <Controller
                      control={control}
                      name="administration"
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      as={<Input />}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="organization"
                    error={errors.organization?.id}
                    required={true}
                    label="Organizasyon"
                  >
                    <FetchSelect
                      api="/masterdata/organizations"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field={`organization`}
                      placeholder="Organizasyon seçiniz"
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="BKMYBS No">
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name="bkmybsNumber"
                      as={<Input />}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="BKMYBS Adı">
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name="bkmybsName"
                      as={<Input />}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="BKMYBS Sicil No">
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name="bkmybsRegisterNumber"
                      as={<Input />}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="BKMYBS Muhasebe Kodu">
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name="bkmybsAccountingNumber"
                      as={<Input />}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="BKMYBS Giriş Tarihi">
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name={`bkmybsEntryDate`}
                      as={
                        <DatePicker
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          placeholder="BKMYBS giriş tarihi giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem label="Bağımlı Taşınmaz">
                    <RealEstateSelect
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field={`realEstate`}
                      editingId={realestate.id}
                    />
                  </FormItem>
                </Col>

                <Col xs={24} md={8}>
                  <FormItem
                    htmlFor="legalStatu"
                    required={true}
                    error={errors.legalStatu?.id}
                    label="Malik/Hukuki Durumu"
                  >
                    <FetchSelect
                      api="/masterdata/legalstatus"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field={`legalStatu`}
                      placeholder="Malik/Hukuki durumu seçiniz"
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection
              title="Hissedarlar"
              style={errors.shareHolders ? { color: "red" } : null}
              error={errors.shareHolders?.message}
            >
              <Row gutter={16}>
                <Col xs={24}>
                  <ShareholderList form={form} mode={mode} />
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Tapu Bilgileri">
              <Row gutter={16}>
                {deeds.map((item, index) => renderDeedFields(index))}
              </Row>
            </FormSection>

            {[
              RULES.RULE0009,
              RULES.RULE0011,
              RULES.RULE0012,
              RULES.RULE0013,
            ].includes(selectedDeedType?.root?.rule?.code) ? (
              <FormSection title="Edinme Bilgileri">
                <Row gutter={16}>
                  <Col xs={24}>
                    <FormItem
                      htmlFor="acquisitionFigure"
                      error={errors.acquisitionFigure}
                      label="Edinme Şekli"
                    >
                      <FetchSelect
                        api="/masterdata/acquisitionfigures"
                        form={form}
                        readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        field="acquisitionFigure"
                        placeholder="Edinme şekli seçiniz"
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={16}>
                    <FormItem
                      label="Edinme Tarihi"
                      error={errors.dateOfAcquisition}
                    >
                      <Controller
                        control={form.control}
                        name={`dateOfAcquisition`}
                        readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        as={
                          <DatePicker
                            format="DD.MM.YYYY"
                            style={{ width: "100%" }}
                            placeholder="Edinme tarihi giriniz"
                          />
                        }
                      />
                    </FormItem>
                  </Col>
                  <Col xs={24} md={8}>
                    <FormItem
                      label="Edinme Süresi"
                      error={errors.durationOfAcquisition}
                    >
                      <Controller
                        control={form.control}
                        readOnly={mode === DISPLAY_MODE.READ_ONLY}
                        name={`durationOfAcquisition`}
                        as={<Input placeholder="Edinme Süresini Giriniz" />}
                      />
                    </FormItem>
                  </Col>
                </Row>
              </FormSection>
            ) : null}

            <FormSection title="Arşiv Bilgileri">
              <Row gutter={16}>
                <Col xs={24} sm={16}>
                  <FormItem
                    label="Arşivleme Tarihi"
                    error={errors.dateOfArchive}
                  >
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name={`dateOfArchive`}
                      as={
                        <DatePicker
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          placeholder="Arşivleme tarihi giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} sm={8}>
                  <FormItem label="Arşiv No." error={errors.archiveNumber}>
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name={`archiveNumber`}
                      as={<Input placeholder="Arşiv No. giriniz" />}
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            <FormSection title="Çıkış Bilgileri">
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <FormItem
                    label="Kayıtlardan Çıkış Tarihi"
                    error={errors.dateOfOutput}
                  >
                    <Controller
                      control={form.control}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      name={`dateOfOutput`}
                      as={
                        <DatePicker
                          format="DD.MM.YYYY"
                          style={{ width: "100%" }}
                          placeholder="Kayıtlardan çıkış tarihi giriniz"
                        />
                      }
                    />
                  </FormItem>
                </Col>
                <Col xs={24} md={12}>
                  <FormItem
                    htmlFor="outputShape"
                    error={errors.outputShape}
                    label="Kayıtlardan Çıkış Nedeni"
                  >
                    <FetchSelect
                      api="/masterdata/outputshapes"
                      form={form}
                      readOnly={mode === DISPLAY_MODE.READ_ONLY}
                      field={`outputShape`}
                      placeholder="Kayıtlardan çıkış nedeni seçiniz"
                    />
                  </FormItem>
                </Col>
              </Row>
            </FormSection>
            {mode === DISPLAY_MODE.READ_ONLY ? null : (
              <Row
                justify="space-between"
                style={{
                  borderTop: "1px dashed rgb(166, 157, 152)",
                  position: "sticky",
                  bottom: 0,
                  padding: "16px 0",
                  backgroundColor: "#f2f2f2",
                }}
              >
                <Col>
                  <Button
                    loading={
                      store.process === ACTION_TYPES.CREATING_DATA ||
                      store.process === ACTION_TYPES.UPDATING_DATA
                    }
                    style={{ width: 120, marginRight: 8 }}
                    type="primary"
                    htmlType="submit"
                  >
                    Kaydet
                  </Button>
                  <Button
                    onClick={() => {
                      Modal.confirm({
                        title: "İptal",
                        content:
                          "Kaydetmediğiniz tüm değişiklikler, geri alınacaktır.",
                        okText: "Evet",
                        cancelText: "İptal",
                        onOk: () => {
                          reset();
                        },
                      });
                    }}
                  >
                    İptal
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </FormLayout>
      </form>
    </Spin>
  );
}
